/**
 * Appearance preferences: theme and table density.
 *
 * Both are properties of this BROWSER, not of the account or the organization,
 * so they live in localStorage and never touch the API. The inline script in
 * index.html applies them before first paint — without it, a dark-mode user
 * would see a white flash on every reload — and this module keeps them in
 * step afterwards.
 *
 *   html[data-theme='light' | 'dark']       always resolved; 'system' follows the OS
 *   html[data-density='compact']            only present when compact
 *
 * Plain functions plus a store, so the React hooks in useTheme.js are a thin
 * layer and the external portals (which never mount the shell) still get the
 * right theme from the inline script alone.
 */

const THEME_KEY = 'nexerp.theme';
const DENSITY_KEY = 'nexerp.density';

export const THEMES = ['light', 'dark', 'system'];
export const DENSITIES = ['comfortable', 'compact'];

const listeners = new Set();
const notify = () => listeners.forEach((listener) => listener());

export function subscribeAppearance(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function read(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key, value) {
  try {
    if (value == null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Storage disabled (private browsing): the choice lasts for this page only.
  }
}

const systemDark = () =>
  typeof window !== 'undefined' && Boolean(window.matchMedia?.('(prefers-color-scheme: dark)').matches);

const reducedMotion = () =>
  typeof window !== 'undefined' && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

// --- theme ------------------------------------------------------------------

export function getThemePreference() {
  const stored = read(THEME_KEY);
  return THEMES.includes(stored) ? stored : 'system';
}

export function resolveTheme(preference = getThemePreference()) {
  if (preference === 'light' || preference === 'dark') return preference;
  return systemDark() ? 'dark' : 'light';
}

/** The theme currently on the page. */
export function getResolvedTheme() {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

function paint(theme) {
  document.documentElement.dataset.theme = theme;
  // The browser chrome (mobile address bar, PWA title bar) follows along.
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'dark' ? '#0c0d11' : '#f5f6f8');
  // Announced from HERE, not from the caller: a view transition paints
  // asynchronously, and a listener told earlier would read the old theme.
  notify();
}

/**
 * Applies a theme, animated.
 *
 * With the View Transitions API the new theme is revealed as a circle growing
 * from `origin` (the toggle that was pressed); without it, colours ease across
 * for 220ms. Under prefers-reduced-motion it simply switches.
 */
function applyTheme(theme, origin) {
  if (getResolvedTheme() === theme && document.documentElement.dataset.theme) return;

  if (reducedMotion()) {
    paint(theme);
    return;
  }

  if (typeof document.startViewTransition === 'function') {
    const transition = document.startViewTransition(() => paint(theme));
    transition.ready
      .then(() => {
        const x = origin?.x ?? window.innerWidth / 2;
        const y = origin?.y ?? 0;
        const radius = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y));
        document.documentElement.animate(
          { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`] },
          { duration: 460, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', pseudoElement: '::view-transition-new(root)' }
        );
      })
      .catch(() => {});
    return;
  }

  const root = document.documentElement;
  root.classList.add('theme-switching');
  paint(theme);
  window.setTimeout(() => root.classList.remove('theme-switching'), 260);
}

/**
 * @param {'light'|'dark'|'system'} preference
 * @param {{x:number,y:number}} [origin] where the reveal starts, in viewport px
 */
export function setThemePreference(preference, origin) {
  if (!THEMES.includes(preference)) return;
  write(THEME_KEY, preference === 'system' ? null : preference);
  applyTheme(resolveTheme(preference), origin);
  notify();
}

// Follow the OS while the preference is 'system'.
if (typeof window !== 'undefined' && window.matchMedia) {
  const query = window.matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => {
    if (getThemePreference() !== 'system') return;
    applyTheme(resolveTheme('system'));
    notify();
  };
  query.addEventListener?.('change', onChange);
}

// --- density ----------------------------------------------------------------

export function getDensity() {
  return read(DENSITY_KEY) === 'compact' ? 'compact' : 'comfortable';
}

export function setDensity(density) {
  const next = density === 'compact' ? 'compact' : 'comfortable';
  write(DENSITY_KEY, next === 'compact' ? 'compact' : null);
  if (next === 'compact') document.documentElement.dataset.density = 'compact';
  else delete document.documentElement.dataset.density;
  notify();
}
